import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import api from '../services/api';
import { OrderProduct, ProductFieldDefinition } from '../types/order';
import StaffOrderProductFieldsForm, { StaffOrderFieldValues } from './StaffOrderProductFieldsForm';
import { orderProductMatchesServiceTypeFilter } from '../constants/staffOrderCatalogFilter';
import { orderProductNeedsInstanceServiceAddress } from '../constants/staffOrderServiceAddress';
import SupaSQAddressField from './order/SupaSQAddressField';

export type StaffOrderCartLinePayload = {
  product: OrderProduct;
  instances: StaffOrderFieldValues[];
  portingFiles?: (File | null)[];
};

export function parseDIDCountFromRange(rangeText: string): number {
  if (!rangeText.trim()) return 0;
  let total = 0;
  const parts = rangeText.split(/[\n,]+/).map((s) => s.trim()).filter(Boolean);
  for (const part of parts) {
    const rangeMatch = part.match(/(\d+)\s*[–\-]\s*(\d+)\s*$/);
    if (rangeMatch) {
      const startStr = rangeMatch[1];
      const endStr = rangeMatch[2];
      const startNum = parseInt(startStr, 10);
      const endNum = parseInt(endStr, 10);
      if (endNum >= startNum) {
        // Full range: e.g. "0243220072-0243220075"
        total += endNum - startNum + 1;
      } else if (endStr.length < startStr.length) {
        // Suffix shorthand: e.g. "0243220072-75" → end is "0243220075"
        const prefix = startStr.slice(0, startStr.length - endStr.length);
        const actualEnd = parseInt(prefix + endStr, 10);
        total += actualEnd >= startNum ? actualEnd - startNum + 1 : 1;
      } else {
        total += 1;
      }
    } else if (/\d/.test(part)) {
      total += 1;
    }
  }
  return total;
}

export type StaffOrderWizardCompleteOptions = {
  replaceRowId?: string;
};

type StaffOrderAddProductWizardProps = {
  open: boolean;
  onClose: () => void;
  products: OrderProduct[];
  serviceTypes: any[];
  serviceTypeFilter: string;
  onServiceTypeFilterChange: (code: string) => void;
  /** Map order_subcategories.id → slug (for CRM service-type filter when product.service_type_code is unset). */
  subcategorySlugById?: Record<string, string>;
  /** Account / form default service address for NBN, mobile, voice, Supa (per-unit picker). */
  profileServiceAddress?: string;
  /** When set, wizard opens in edit mode for this cart line (product and unit count fixed). */
  editContext?: { rowId: string; line: StaffOrderCartLinePayload } | null;
  onComplete: (line: StaffOrderCartLinePayload, options?: StaffOrderWizardCompleteOptions) => void;
};

type Step = 'pick' | 'configure';

/** Pre-fill per-unit field values from the catalog product (plan_features + top-level columns). Skips `features`. */
function buildInstanceDefaultsFromProduct(
  product: OrderProduct,
  defs: ProductFieldDefinition[],
): StaffOrderFieldValues {
  const pf = product.plan_features || {};
  const keys = new Set(defs.map((d) => d.field_key).filter((k) => k && k !== 'features'));
  const out: StaffOrderFieldValues = {};

  const setIf = (fieldKey: string, value: unknown) => {
    if (!keys.has(fieldKey)) return;
    if (value === undefined || value === null) return;
    const s = typeof value === 'string' ? value : String(value);
    if (s.trim() === '') return;
    out[fieldKey] = s;
  };

  setIf('name', product.name);
  setIf('description', product.description ?? pf.description);

  const agreement =
    product.contract_terms ??
    pf.contract_terms ??
    pf.agreement_term ??
    pf.agreement_terms ??
    pf.contract_term;
  setIf('contract_terms', agreement);

  const mpNum = product.monthly_price;
  if (mpNum != null && Number.isFinite(Number(mpNum)) && keys.has('monthly_price')) {
    out.monthly_price = String(mpNum);
  } else if (pf.monthly_price != null && String(pf.monthly_price).trim() !== '' && keys.has('monthly_price')) {
    out.monthly_price = String(pf.monthly_price).trim();
  }

  const spNum = product.setup_price;
  if (spNum != null && Number.isFinite(Number(spNum)) && keys.has('setup_price')) {
    out.setup_price = String(spNum);
  } else if (pf.setup_price != null && String(pf.setup_price).trim() !== '' && keys.has('setup_price')) {
    out.setup_price = String(pf.setup_price).trim();
  }

  for (const [k, v] of Object.entries(pf)) {
    if (k === 'features') continue;
    if (['name', 'description', 'contract_terms', 'monthly_price', 'setup_price'].includes(k)) continue;
    setIf(k, v);
  }

  return out;
}

type StaffCatalogServiceAddressFieldsProps = {
  profileServiceAddress: string;
  value: string;
  onChange: (next: string) => void;
};

function StaffCatalogServiceAddressFields({
  profileServiceAddress,
  value,
  onChange,
}: StaffCatalogServiceAddressFieldsProps) {
  const profileTrim = profileServiceAddress.trim();
  const valTrim = value.trim();
  const usingProfile = profileTrim.length > 0 && valTrim === profileTrim;
  const showCustomField = !profileTrim || !usingProfile;

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Service address
      </Typography>
      {profileTrim ? (
        <FormControl fullWidth size="small" sx={{ mb: showCustomField ? 1.5 : 0 }}>
          <InputLabel id="staff-svc-addr-src">Address source</InputLabel>
          <Select
            labelId="staff-svc-addr-src"
            label="Address source"
            value={usingProfile ? 'profile' : 'custom'}
            onChange={(e) => {
              if (e.target.value === 'profile') {
                onChange(profileTrim);
              } else {
                onChange(usingProfile ? '' : value);
              }
            }}
          >
            <MenuItem value="profile">Use account profile address</MenuItem>
            <MenuItem value="custom">Enter a different service address…</MenuItem>
          </Select>
          <FormHelperText>
            Stored on this catalog line for provisioning (not on the main new-order form for this service type).
          </FormHelperText>
        </FormControl>
      ) : null}
      {showCustomField ? (
        <TextField
          fullWidth
          multiline
          minRows={2}
          size="small"
          required
          label={profileTrim ? 'Service address' : 'Service address (required)'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Street, suburb, state, postcode"
        />
      ) : null}
    </Box>
  );
}

function isMobileProduct(product: OrderProduct): boolean {
  return (product.service_type_code || '').toLowerCase() === 'mobile';
}

export function isVoiceProduct(product: OrderProduct): boolean {
  return (product.service_type_code || '').toLowerCase() === 'voice';
}

export function isVoiceBusinessProduct(
  product: OrderProduct,
  subcategorySlugById: Record<string, string>,
): boolean {
  return isVoiceProduct(product) &&
    (subcategorySlugById[product.subcategory_id || ''] || '') === 'voice-business';
}

type MobilePortingFieldsProps = {
  values: StaffOrderFieldValues;
  onChange: (next: StaffOrderFieldValues) => void;
  portingFile: File | null;
  onPortingFileChange: (f: File | null) => void;
};

function MobilePortingFields({ values, onChange, portingFile, onPortingFileChange }: MobilePortingFieldsProps) {
  const numberType = values.number_type || 'new_number';

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Mobile number type
      </Typography>
      <RadioGroup
        row
        value={numberType}
        onChange={(e) => onChange({ ...values, number_type: e.target.value })}
      >
        <FormControlLabel value="new_number" control={<Radio size="small" />} label="New number" />
        <FormControlLabel value="port_in" control={<Radio size="small" />} label="Port in existing number" />
      </RadioGroup>

      {numberType === 'new_number' && (
        <Alert severity="info" sx={{ mt: 1.5 }}>
          A new mobile number will be assigned during the provisioning process.
        </Alert>
      )}

      {numberType === 'port_in' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1.5 }}>
          <TextField
            label="Mobile number to port"
            size="small"
            fullWidth
            required
            value={values.porting_mobile_number || ''}
            onChange={(e) => onChange({ ...values, porting_mobile_number: e.target.value })}
            placeholder="04XX XXX XXX"
          />
          <TextField
            label="Current service provider"
            size="small"
            fullWidth
            required
            value={values.porting_current_provider || ''}
            onChange={(e) => onChange({ ...values, porting_current_provider: e.target.value })}
            placeholder="e.g. Telstra, Optus, Vodafone"
          />
          <TextField
            label="Current account number"
            size="small"
            fullWidth
            required
            value={values.porting_current_account_number || ''}
            onChange={(e) => onChange({ ...values, porting_current_account_number: e.target.value })}
          />
          <Box>
            <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
              Upload current account / latest bill <span style={{ color: 'red' }}>*</span>
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              The document must clearly show the service (mobile) number and account number.
            </Typography>
            <Box display="flex" alignItems="center" gap={1.5}>
              <Button component="label" variant="outlined" startIcon={<UploadFileIcon />} size="small">
                Choose file
                <input
                  type="file"
                  hidden
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => {
                    onPortingFileChange(e.target.files?.[0] ?? null);
                    e.target.value = '';
                  }}
                />
              </Button>
              {portingFile ? (
                <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 220 }}>
                  {portingFile.name}
                </Typography>
              ) : (
                <Typography variant="body2" color="error">No file chosen</Typography>
              )}
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
}

type VoicePortingFieldsProps = {
  values: StaffOrderFieldValues;
  onChange: (next: StaffOrderFieldValues) => void;
  portingFile: File | null;
  onPortingFileChange: (f: File | null) => void;
};

function VoicePortingFields({ values, onChange, portingFile, onPortingFileChange }: VoicePortingFieldsProps) {
  const numberType = values.number_type || 'new_number';

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Fixed number type
      </Typography>
      <RadioGroup
        row
        value={numberType}
        onChange={(e) => onChange({ ...values, number_type: e.target.value })}
      >
        <FormControlLabel value="new_number" control={<Radio size="small" />} label="New number" />
        <FormControlLabel value="port_in" control={<Radio size="small" />} label="Port in existing number" />
      </RadioGroup>

      {numberType === 'new_number' && (
        <Alert severity="info" sx={{ mt: 1.5 }}>
          A new fixed number will be assigned during the provisioning process.
        </Alert>
      )}

      {numberType === 'port_in' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1.5 }}>
          <TextField
            label="Fixed number(s) to port"
            size="small"
            fullWidth
            required
            multiline
            minRows={2}
            value={values.porting_fixed_numbers || ''}
            onChange={(e) => onChange({ ...values, porting_fixed_numbers: e.target.value })}
            helperText="Enter a single number or a range, e.g. 02 1234 5678–5689"
          />
          <TextField
            label="Current service provider"
            size="small"
            fullWidth
            required
            value={values.porting_current_provider || ''}
            onChange={(e) => onChange({ ...values, porting_current_provider: e.target.value })}
            placeholder="e.g. Telstra, Optus, TPG"
          />
          <TextField
            label="Current account number"
            size="small"
            fullWidth
            required
            value={values.porting_current_account_number || ''}
            onChange={(e) => onChange({ ...values, porting_current_account_number: e.target.value })}
          />
          <Box>
            <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
              Upload current account / latest bill <span style={{ color: 'red' }}>*</span>
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              The document must clearly show the fixed number(s) and account number.
            </Typography>
            <Box display="flex" alignItems="center" gap={1.5}>
              <Button component="label" variant="outlined" startIcon={<UploadFileIcon />} size="small">
                Choose file
                <input
                  type="file"
                  hidden
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => {
                    onPortingFileChange(e.target.files?.[0] ?? null);
                    e.target.value = '';
                  }}
                />
              </Button>
              {portingFile ? (
                <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 220 }}>
                  {portingFile.name}
                </Typography>
              ) : (
                <Typography variant="body2" color="error">No file chosen</Typography>
              )}
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
}

function StaffOrderAddProductWizard({
  open,
  onClose,
  products,
  serviceTypes,
  serviceTypeFilter,
  onServiceTypeFilterChange,
  subcategorySlugById = {},
  profileServiceAddress = '',
  editContext,
  onComplete,
}: StaffOrderAddProductWizardProps) {
  const [step, setStep] = useState<Step>('pick');
  const [productId, setProductId] = useState('');
  const [quantityStr, setQuantityStr] = useState('1');
  const [instanceIndex, setInstanceIndex] = useState(0);
  const [instances, setInstances] = useState<StaffOrderFieldValues[]>([]);
  const [portingFiles, setPortingFiles] = useState<(File | null)[]>([]);
  const [fieldDefs, setFieldDefs] = useState<ProductFieldDefinition[]>([]);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [stepErr, setStepErr] = useState<string | null>(null);
  const defaultsMergeKeyRef = useRef('');

  const fieldDefsForForm = useMemo(
    () => fieldDefs.filter((f) => f.field_key !== 'features' && f.is_visible),
    [fieldDefs],
  );

  const filteredProducts = useMemo(() => {
    if (!serviceTypeFilter) return products;
    return products.filter((p) => orderProductMatchesServiceTypeFilter(p, serviceTypeFilter, subcategorySlugById));
  }, [products, serviceTypeFilter, subcategorySlugById]);

  const pickedProduct = useMemo(
    () => filteredProducts.find((p) => p.id === productId) || products.find((p) => p.id === productId) || null,
    [filteredProducts, products, productId],
  );

  const reset = useCallback(() => {
    setStep('pick');
    setProductId('');
    setQuantityStr('1');
    setInstanceIndex(0);
    setInstances([]);
    setPortingFiles([]);
    setFieldDefs([]);
    setLoadErr(null);
    setStepErr(null);
    defaultsMergeKeyRef.current = '';
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    defaultsMergeKeyRef.current = '';
    if (editContext) {
      const { line } = editContext;
      setStep('configure');
      setProductId(line.product.id);
      setQuantityStr(String(line.instances.length));
      setInstances(line.instances.map((inst) => ({ ...inst })));
      setPortingFiles(Array.from({ length: line.instances.length }, (_, i) => line.portingFiles?.[i] ?? null));
      setInstanceIndex(0);
      setFieldDefs([]);
      setLoadErr(null);
      setStepErr(null);
    } else {
      reset();
    }
  }, [open, editContext, reset]);

  useEffect(() => {
    if (!open || step !== 'configure' || !pickedProduct) return;
    const categoryId = pickedProduct.category_id;
    if (!categoryId) {
      setFieldDefs([]);
      setLoadErr(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoadErr(null);
        const rows = await api.adminResolveProductFieldDefinitions(
          categoryId,
          pickedProduct.subcategory_id || undefined,
        );
        if (!cancelled) setFieldDefs(Array.isArray(rows) ? rows : []);
      } catch (e: unknown) {
        if (!cancelled) {
          setFieldDefs([]);
          setLoadErr((e as Error)?.message || 'Failed to load product fields');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, step, pickedProduct?.id, pickedProduct?.category_id, pickedProduct?.subcategory_id]);

  useEffect(() => {
    if (!open || step !== 'configure' || !pickedProduct || !orderProductNeedsInstanceServiceAddress(pickedProduct, subcategorySlugById)) {
      return;
    }
    const prof = profileServiceAddress.trim();
    if (!prof) return;
    setInstances((prev) =>
      prev.map((inst) => {
        const cur = String(inst.service_address ?? '').trim();
        return cur ? inst : { ...inst, service_address: prof };
      }),
    );
  }, [open, step, pickedProduct?.id, pickedProduct?.service_type_code, profileServiceAddress, subcategorySlugById]);

  useEffect(() => {
    if (!open || step !== 'configure' || !pickedProduct || fieldDefsForForm.length === 0) return;
    const defsSig = fieldDefsForForm.map((d) => d.id).join('|');
    const mergeKey = `${pickedProduct.id}:${editContext?.rowId ?? 'new'}:${defsSig}`;
    if (defaultsMergeKeyRef.current === mergeKey) return;
    defaultsMergeKeyRef.current = mergeKey;
    const defaults = buildInstanceDefaultsFromProduct(pickedProduct, fieldDefsForForm);
    setInstances((prev) => prev.map((inst) => ({ ...defaults, ...inst })));
  }, [
    open,
    step,
    pickedProduct,
    pickedProduct?.id,
    pickedProduct?.monthly_price,
    pickedProduct?.setup_price,
    pickedProduct?.name,
    pickedProduct?.description,
    pickedProduct?.contract_terms,
    pickedProduct?.plan_features,
    fieldDefsForForm,
    editContext?.rowId,
  ]);

  const currentValues = instances[instanceIndex] || {};

  const setCurrentValues = (next: StaffOrderFieldValues) => {
    setInstances((prev) => {
      const copy = [...prev];
      copy[instanceIndex] = next;
      return copy;
    });
  };

  const handlePickNext = () => {
    setStepErr(null);
    if (!pickedProduct) {
      setStepErr('Select a catalog product.');
      return;
    }
    const q = parseInt(quantityStr, 10);
    if (!Number.isFinite(q) || q < 1 || q > 50) {
      setStepErr('Quantity must be between 1 and 50.');
      return;
    }
    const needs = pickedProduct && orderProductNeedsInstanceServiceAddress(pickedProduct, subcategorySlugById);
    const prof = (profileServiceAddress || '').trim();
    const isMobile = isMobileProduct(pickedProduct);
    const isVoice = isVoiceProduct(pickedProduct);
    const isVoiceBusiness = isVoiceBusinessProduct(pickedProduct, subcategorySlugById);
    setInstances(Array.from({ length: q }, () => ({
      ...(needs && prof ? { service_address: prof } : {}),
      ...(isMobile ? { number_type: 'new_number' } : {}),
      ...(isVoice ? { number_type: 'new_number', did_count: '' } : {}),
      ...(isVoiceBusiness ? { sip_channels: '1' } : {}),
    })));
    setPortingFiles(Array.from({ length: q }, () => null));
    setInstanceIndex(0);
    setStep('configure');
  };

  const handleConfigureNext = () => {
    setStepErr(null);
    if (!pickedProduct) return;
    if (orderProductNeedsInstanceServiceAddress(pickedProduct, subcategorySlugById)) {
      const s = String(instances[instanceIndex]?.service_address ?? '').trim();
      if (s.length < 3) {
        setStepErr('Service address must be at least 3 characters.');
        return;
      }
    }
    if (isMobileProduct(pickedProduct)) {
      const cur = instances[instanceIndex] || {};
      const numType = (cur.number_type || 'new_number');
      if (numType === 'port_in') {
        if (!String(cur.porting_mobile_number ?? '').trim()) {
          setStepErr('Enter the mobile number to port.');
          return;
        }
        if (!String(cur.porting_current_provider ?? '').trim()) {
          setStepErr('Enter the current service provider.');
          return;
        }
        if (!String(cur.porting_current_account_number ?? '').trim()) {
          setStepErr('Enter the current account number.');
          return;
        }
        if (!portingFiles[instanceIndex]) {
          setStepErr('Upload the current account / latest bill document.');
          return;
        }
      }
    }
    if (isVoiceProduct(pickedProduct)) {
      const cur = instances[instanceIndex] || {};
      const numType = (cur.number_type || 'new_number');
      if (numType === 'port_in') {
        if (!String(cur.porting_fixed_numbers ?? '').trim()) {
          setStepErr('Enter the fixed number(s) to port.');
          return;
        }
        if (!String(cur.porting_current_provider ?? '').trim()) {
          setStepErr('Enter the current service provider.');
          return;
        }
        if (!String(cur.porting_current_account_number ?? '').trim()) {
          setStepErr('Enter the current account number.');
          return;
        }
        if (!portingFiles[instanceIndex]) {
          setStepErr('Upload the current account / latest bill document.');
          return;
        }
      }
    }
    if (instanceIndex + 1 >= instances.length) {
      onComplete(
        { product: pickedProduct, instances, portingFiles },
        editContext ? { replaceRowId: editContext.rowId } : undefined,
      );
      onClose();
      reset();
      return;
    }
    setInstanceIndex((i) => i + 1);
  };

  const handleConfigureBack = () => {
    setStepErr(null);
    if (instanceIndex > 0) {
      setInstanceIndex((i) => i - 1);
      return;
    }
    if (editContext) {
      onClose();
      return;
    }
    setStep('pick');
  };

  const isEdit = Boolean(editContext);
  const title = isEdit
    ? `Edit cart line — ${pickedProduct?.name ?? 'Product'} (${instanceIndex + 1} of ${instances.length})`
    : step === 'pick'
      ? 'Add catalog product'
      : `Configure — ${pickedProduct?.name ?? 'Product'} (${instanceIndex + 1} of ${instances.length})`;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {step === 'pick' && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Choose a CRM service type to narrow products, pick an active catalog product, then set how many
              separate services you are adding (each unit can have its own details).
            </Typography>
            {stepErr && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setStepErr(null)}>
                {stepErr}
              </Alert>
            )}
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth size="small" disabled={isEdit}>
                  <InputLabel>CRM service type</InputLabel>
                  <Select
                    label="CRM service type"
                    value={serviceTypeFilter}
                    onChange={(e) => {
                      onServiceTypeFilterChange(e.target.value as string);
                      setProductId('');
                    }}
                  >
                    <MenuItem value="">All (any active product)</MenuItem>
                    {serviceTypes.map((t: any) => (
                      <MenuItem key={t.id} value={t.code}>
                        {t.name} ({t.code})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth required size="small" disabled={isEdit}>
                  <InputLabel>Product</InputLabel>
                  <Select
                    label="Product"
                    value={productId}
                    onChange={(e) => setProductId(e.target.value)}
                    disabled={isEdit || filteredProducts.length === 0}
                  >
                    <MenuItem value="">
                      <em>{filteredProducts.length === 0 ? 'No products match' : 'Select product'}</em>
                    </MenuItem>
                    {filteredProducts.map((p) => (
                      <MenuItem key={p.id} value={p.id}>
                        {p.name}
                        {p.monthly_price != null ? ` — $${Number(p.monthly_price).toFixed(2)}/mo` : ''}
                        {p.service_type_code ? ` (${p.service_type_code})` : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Quantity"
                  type="number"
                  disabled={isEdit}
                  inputProps={{ min: 1, max: 50 }}
                  value={quantityStr}
                  onChange={(e) => setQuantityStr(e.target.value)}
                  helperText="Each unit is configured separately on the next steps."
                />
              </Grid>
            </Grid>
          </>
        )}
        {step === 'configure' && pickedProduct && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Fields come from <strong>Category Management → Product fields</strong>. Name, description, agreement term,
              and prices are pre-filled from the catalog product where those keys exist (you can change per unit).
            </Typography>
            {loadErr && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {loadErr}
              </Alert>
            )}
            {stepErr && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setStepErr(null)}>
                {stepErr}
              </Alert>
            )}
            {pickedProduct && orderProductNeedsInstanceServiceAddress(pickedProduct, subcategorySlugById) ? (
              (pickedProduct.service_type_code || '').toLowerCase() === 'supa_networks' ? (
                <SupaSQAddressField
                  value={String(currentValues.service_address ?? '')}
                  onChange={(addr) => setCurrentValues({ ...currentValues, service_address: addr })}
                />
              ) : (
                <StaffCatalogServiceAddressFields
                  profileServiceAddress={profileServiceAddress}
                  value={String(currentValues.service_address ?? '')}
                  onChange={(addr) => setCurrentValues({ ...currentValues, service_address: addr })}
                />
              )
            ) : null}
            {pickedProduct && isMobileProduct(pickedProduct) ? (
              <MobilePortingFields
                values={currentValues}
                onChange={setCurrentValues}
                portingFile={portingFiles[instanceIndex] ?? null}
                onPortingFileChange={(f) => {
                  setPortingFiles((prev) => {
                    const copy = [...prev];
                    copy[instanceIndex] = f;
                    return copy;
                  });
                }}
              />
            ) : null}
            {pickedProduct && isVoiceProduct(pickedProduct) ? (
              <>
                <VoicePortingFields
                  values={currentValues}
                  onChange={(next) => {
                    const updated = { ...next };
                    if (next.porting_fixed_numbers !== currentValues.porting_fixed_numbers) {
                      const count = parseDIDCountFromRange(String(next.porting_fixed_numbers ?? ''));
                      updated.did_count = count > 0 ? String(count) : updated.did_count;
                    }
                    setCurrentValues(updated);
                  }}
                  portingFile={portingFiles[instanceIndex] ?? null}
                  onPortingFileChange={(f) => {
                    setPortingFiles((prev) => {
                      const copy = [...prev];
                      copy[instanceIndex] = f;
                      return copy;
                    });
                  }}
                />
                {isVoiceBusinessProduct(pickedProduct, subcategorySlugById) && (
                  <TextField
                    label="Number of SIP channels"
                    type="number"
                    size="small"
                    fullWidth
                    required
                    inputProps={{ min: 1, max: 50 }}
                    value={currentValues.sip_channels ?? '1'}
                    onChange={(e) => {
                      const v = Math.min(50, Math.max(1, parseInt(e.target.value, 10) || 1));
                      setCurrentValues({ ...currentValues, sip_channels: String(v) });
                    }}
                    helperText="Each channel × monthly price = recurring charge"
                    sx={{ mb: 2 }}
                  />
                )}
                <TextField
                  label="Number of DIDs"
                  type="number"
                  size="small"
                  fullWidth
                  inputProps={{ min: 0 }}
                  value={currentValues.did_count ?? ''}
                  onChange={(e) => {
                    const v = e.target.value;
                    setCurrentValues({ ...currentValues, did_count: v });
                  }}
                  helperText="DID product will be added to cart automatically"
                  sx={{ mb: 2 }}
                />
              </>
            ) : null}
            <StaffOrderProductFieldsForm fields={fieldDefsForForm} values={currentValues} onChange={setCurrentValues} />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            if (step === 'configure') {
              handleConfigureBack();
            } else {
              onClose();
            }
          }}
        >
          {step === 'configure' ? 'Back' : 'Cancel'}
        </Button>
        {step === 'pick' ? (
          <Button variant="contained" onClick={handlePickNext}>
            Next
          </Button>
        ) : (
          <Button variant="contained" onClick={handleConfigureNext}>
            {instanceIndex + 1 >= instances.length ? (isEdit ? 'Save to cart' : 'Add to cart') : 'Next unit'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default StaffOrderAddProductWizard;
